import { useEffect, useRef } from 'react';
import { useForm } from 'react-final-form';
import { useAppSelector } from 'helpers/hooks';
import { getRecipientsData } from 'selectors/recipients';
import { useFieldValue } from 'hooks/useFormHelpers';
import { AllocationGroup } from './types';

const MAX_GROUPS = 6;

const makeGroup = (overrides: Partial<AllocationGroup> = {}): AllocationGroup => ({
  _clientId: crypto.randomUUID(),
  weight_percent: 0,
  recipient_ids: [],
  ...overrides,
});

export const groupKey = (g: AllocationGroup) => g.id || g._clientId || '';

const arraysShallowEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const useAllocationGroups = () => {
  const { change } = useForm();
  const recipients = useAppSelector(getRecipientsData);
  const recipientsSelected: { value: string; label: string }[] =
    useFieldValue('recipients_selected') || [];
  const recipientGroups: AllocationGroup[] = useFieldValue('recipient_groups') || [];

  const recipientGroupsRef = useRef(recipientGroups);
  recipientGroupsRef.current = recipientGroups;

  const lastSyncedIdsRef = useRef<string[]>([]);

  useEffect(() => {
    if (recipientGroupsRef.current.length === 1) {
      change('recipient_groups', [...recipientGroupsRef.current, makeGroup()]);
    }
  }, []);

  useEffect(() => {
    if (recipientsSelected.length === 0) return;

    const currentIds = recipientsSelected.map(r => r.value);
    const groups = recipientGroupsRef.current;

    if (groups.length === 0) {
      if (recipients.length === 0) return;
      const sortedIds = [...recipientsSelected]
        .sort((a, b) => {
          const recA = recipients.find(r => r.recipient_id === a.value);
          const recB = recipients.find(r => r.recipient_id === b.value);
          return parseFloat(recB?.impact_score || '0') - parseFloat(recA?.impact_score || '0');
        })
        .map(r => r.value);
      lastSyncedIdsRef.current = currentIds;
      change('recipient_groups', [makeGroup({ recipient_ids: sortedIds }), makeGroup()]);
      return;
    }

    if (arraysShallowEqual(lastSyncedIdsRef.current, currentIds)) return;
    lastSyncedIdsRef.current = currentIds;

    const allGroupedIds = groups.flatMap(g => g.recipient_ids);
    const newIds = currentIds.filter(id => !allGroupedIds.includes(id));
    const hasRemovals = allGroupedIds.some(id => !currentIds.includes(id));

    if (newIds.length === 0 && !hasRemovals) return;

    change(
      'recipient_groups',
      groups.map((g, i) => ({
        ...g,
        recipient_ids: [
          ...g.recipient_ids.filter(id => currentIds.includes(id)),
          ...(i === 0 ? newIds : []),
        ],
      }))
    );
  }, [recipientsSelected, recipients]);

  const canAddGroup =
    recipientGroups.length < MAX_GROUPS && recipientGroups.every(g => g.recipient_ids.length > 0);

  const handleAddGroup = () => {
    if (recipientGroups.length >= MAX_GROUPS) return;
    change('recipient_groups', [...recipientGroups, makeGroup()]);
  };

  const handleRemoveGroup = (groupIndex: number) => {
    const orphanedIds = recipientGroups[groupIndex].recipient_ids;
    const updated = recipientGroups
      .filter((_, i) => i !== groupIndex)
      .map((g, i) => (i === 0 ? { ...g, recipient_ids: [...g.recipient_ids, ...orphanedIds] } : g));
    change('recipient_groups', updated);
  };

  const handleMoveRecipient = (
    recipientId: string,
    fromGroupIndex: number,
    toGroupIndex: number
  ) => {
    change(
      'recipient_groups',
      recipientGroups.map((g, i) => {
        if (i === fromGroupIndex) {
          return { ...g, recipient_ids: g.recipient_ids.filter(id => id !== recipientId) };
        }
        if (i === toGroupIndex) {
          return { ...g, recipient_ids: [...g.recipient_ids, recipientId] };
        }
        return g;
      })
    );
  };

  const handleReorderGroup = (groupIndex: number, newOrder: string[]) => {
    change(
      'recipient_groups',
      recipientGroups.map((g, i) => (i === groupIndex ? { ...g, recipient_ids: newOrder } : g))
    );
  };

  const handleGroupPatch = (groupIndex: number, patch: Partial<AllocationGroup>) => {
    change(
      'recipient_groups',
      recipientGroups.map((g, i) => (i === groupIndex ? { ...g, ...patch } : g))
    );
  };

  return {
    recipients,
    recipientsSelected,
    recipientGroups,
    canAddGroup,
    handleAddGroup,
    handleRemoveGroup,
    handleMoveRecipient,
    handleReorderGroup,
    handleGroupPatch,
  };
};
